import { PropsWithChildren, ReactElement, useCallback, useRef, useState, cloneElement, RefObject } from 'react';
import { usePopover } from '@react-aria/overlays';
import { useFocusWithin, useInteractOutside } from '@react-aria/interactions';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { Placement } from '@react-types/overlays';

import { ButtonContextProvider } from '../Button';

import { DialogContent } from './DialogContent.tsx';
import { usePopoverRelativeElement } from './PopoverRelativeElementContext.ts';

export type PopoverProps = {
  'aria-label': string;
  closeDelay?: number;
  openDelay?: number;
  trigger: ReactElement;
  placement: Placement;
  isNonModal?: boolean;
  offset?: number;
  relRef?: RefObject<Element>;
};

/**
 * **NOTE** if relativeRef contains a border then it should also use `box-sizing: border-box` to ensure correct placement logic
 */
export const Popover = ({
  children,
  trigger,
  isNonModal,
  'aria-label': ariaLabel,
  placement = 'bottom',
  openDelay = 0,
  closeDelay = 0,
  offset,
  relRef,
}: PropsWithChildren<PopoverProps>) => {
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const relativeRef = usePopoverRelativeElement();

  const clearTimers = useCallback(() => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current = [];
  }, []);

  const [openedByPress, setOpenedByPress] = useState(false);

  const state = useOverlayTriggerState({
    defaultOpen: false,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        setOpenedByPress(false);
      }
    },
  });

  const open = useCallback(
    (delay = 0) => {
      clearTimers();
      timers.current.push(setTimeout(state.open, delay));
    },
    [clearTimers, state.open]
  );

  const close = useCallback(
    (delay = 0) => {
      clearTimers();
      timers.current.push(setTimeout(state.close, delay));
    },
    [clearTimers, state.close]
  );

  const handleHoverEnd = useCallback(() => {
    if (openedByPress) {
      return;
    }
    close(closeDelay);
  }, [close, closeDelay, openedByPress]);

  const handleHoverStart = useCallback(() => {
    if (openedByPress) {
      return;
    }
    open(openDelay);
  }, [open, openDelay, openedByPress]);

  const handleTriggerPress = useCallback(() => {
    if (state.isOpen) {
      setOpenedByPress(false);
      close();
      return;
    }

    setOpenedByPress(true);
    open();
  }, [open, close, state.isOpen]);

  const { popoverProps } = usePopover(
    {
      placement,
      triggerRef: relRef ?? relativeRef,
      popoverRef,
      isNonModal,
      offset,
    },
    state
  );

  useInteractOutside({
    ref: popoverRef,
    onInteractOutside: () => {
      close();
    },
  });

  const { focusWithinProps } = useFocusWithin({
    onBlurWithin: (e) => {
      if (e.relatedTarget !== triggerRef.current) {
        close();
      }
    },
  });

  return (
    <>
      <ButtonContextProvider
        value={{
          onHoverStart: handleHoverStart,
          onHoverEnd: handleHoverEnd,
          onPress: handleTriggerPress,
        }}
      >
        {cloneElement(trigger, { ref: triggerRef })}
      </ButtonContextProvider>
      {state.isOpen && (
        <DialogContent
          aria-label={ariaLabel}
          onDismiss={close}
          onButtonPress={close}
          popoverProps={{ ...popoverProps, ...focusWithinProps }}
          onHoverEnd={handleHoverEnd}
          onHoverStart={handleHoverStart}
          ref={popoverRef}
          data-testid="popover"
        >
          {children}
        </DialogContent>
      )}
    </>
  );
};
